import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, BadgeCheck, House, Building2, ChevronDown } from 'lucide-react';

/**
 * DiagnosticPartnerCard
 * ---------------------
 * A single serviceable diagnostic partner / AyushPay lab in the booking flow.
 *
 * Props
 *  - vendor    : { id, name, code, home_collection, walkin_collection,
 *                  address?: { address_1, landmark, locality, city, state, pincode } }
 *  - isActive  : boolean — is this the selected partner
 *  - onSelect  : () => void — select this partner
 *  - index     : number — position in the list (drives the entry stagger)
 *
 * The card is one accessible radio: the whole surface is clickable, focusable,
 * and toggles with Enter / Space. Only AyushPay-owned labs carry an `address`;
 * partners (e.g. Healthians) come through with `address: null`. The address row
 * is reserved on every card regardless, so partner and lab cards stay the same
 * height and their collection badges line up.
 */

// Collapse the address into a single line, hiding empty fields and never
// emitting stray commas:  "<addr_1>, <landmark>, <locality>, <city>, <state> - <pincode>"
const buildAddressLine = (address) => {
  if (!address) return '';
  const main = [address.address_1, address.landmark, address.locality, address.city, address.state]
    .map((p) => (p || '').trim())
    .filter(Boolean)
    .join(', ');
  const pincode = (address.pincode || '').trim();
  if (!pincode) return main;
  return main ? `${main} - ${pincode}` : pincode;
};

const CollectionBadge = ({ active, tone, Icon, label }) => {
  const tones = {
    emerald: active
      ? 'bg-emerald-100 text-emerald-700 ring-emerald-200'
      : 'bg-emerald-50 text-emerald-600 ring-emerald-100',
    indigo: active
      ? 'bg-indigo-100 text-indigo-700 ring-indigo-200'
      : 'bg-indigo-50 text-indigo-600 ring-indigo-100',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${tones[tone]}`}>
      <Icon size={11} strokeWidth={2.2} /> {label}
    </span>
  );
};

const DiagnosticPartnerCard = ({ vendor, isActive, onSelect, index = 0 }) => {
  const addressLine = useMemo(() => buildAddressLine(vendor.address), [vendor.address]);

  const [expanded, setExpanded] = useState(false);
  const textRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);

  // Only offer "Show more" when the one-line address actually overflows — measured,
  // not guessed — so short addresses stay clean and toggle-free. Re-measures on
  // container resize (responsive) and skips while expanded (text wraps, can't overflow).
  useEffect(() => {
    if (expanded) return undefined;
    const el = textRef.current;
    if (!el) return undefined;
    const measure = () => setIsTruncated(el.scrollWidth > el.clientWidth + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [addressLine, expanded]);

  const showToggle = !!addressLine && (expanded || isTruncated);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <motion.div
      role="radio"
      aria-checked={isActive}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={handleKeyDown}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0, scale: isActive ? 1.01 : 1 }}
      transition={{
        opacity: { duration: 0.25, ease: 'easeOut', delay: index * 0.04 },
        y: { duration: 0.25, ease: 'easeOut', delay: index * 0.04 },
        scale: { duration: 0.18, ease: 'easeOut' },
      }}
      whileHover={{ y: -2 }}
      className={`group w-full cursor-pointer select-none rounded-2xl px-4 py-3.5 outline-none transition-[box-shadow,background-color,border-color] duration-200 focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-1 ${
        isActive
          ? 'bg-gradient-to-br from-orange-50 to-amber-50/60 ring-2 ring-orange-400 ring-offset-1 shadow-md shadow-orange-100'
          : 'bg-white ring-1 ring-gray-200 hover:bg-orange-50/40 hover:ring-orange-300 hover:shadow-md'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-sm font-bold transition-colors ${
          isActive ? 'bg-orange-500 text-white shadow-sm shadow-orange-200' : 'bg-gray-100 text-gray-500 group-hover:bg-orange-100 group-hover:text-orange-500'
        }`}>
          {vendor.name?.charAt(0).toUpperCase()}
        </div>

        <div className="min-w-0 flex-1">
          {/* Lab name — primary focus */}
          <div className="flex items-center gap-1.5">
            <p className={`truncate text-sm font-semibold ${isActive ? 'text-orange-900' : 'text-gray-800'}`} title={vendor.name}>
              {vendor.name}
            </p>
            {(vendor.service_model === 'national' || vendor.is_national) && (
              <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-600 ring-1 ring-blue-100 shrink-0">
                <MapPin size={10} strokeWidth={2.4} /> National
              </span>
            )}
          </div>

          {/* Serviceability */}
          <p className="mt-0.5 flex items-center gap-1 text-xs font-medium text-emerald-600">
            <BadgeCheck size={13} strokeWidth={2.3} className="shrink-0" />
            Serviceable at selected address
          </p>

          {/* Address — one clean line by default; "Show more" expands to the full
              wrapped address (only when it actually overflows). The row is reserved
              on every card (min height) so partner and lab cards stay the same
              height and their badges line up. */}
          <div className="mt-1.5">
            <motion.div
              layout
              className="flex items-start gap-1 text-[13px] leading-snug text-gray-500"
              style={{ minHeight: 18 }}
            >
              {addressLine && (
                <>
                  <MapPin size={12} strokeWidth={2.3} className="mt-[2px] shrink-0 text-gray-400" />
                  <span
                    ref={textRef}
                    title={addressLine}
                    className={`min-w-0 flex-1 ${expanded ? 'whitespace-normal break-words' : 'truncate'}`}
                  >
                    {addressLine}
                  </span>
                </>
              )}
            </motion.div>
            {showToggle && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
                aria-expanded={expanded}
                className="mt-0.5 inline-flex items-center gap-0.5 rounded text-[11px] font-semibold text-orange-500 outline-none hover:text-orange-600 focus-visible:ring-2 focus-visible:ring-orange-300"
              >
                {expanded ? 'Show less' : 'Show more'}
                <ChevronDown size={12} strokeWidth={2.5} className={`transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
              </button>
            )}
          </div>

          {/* Collection badges */}
          {(vendor.home_collection || vendor.walkin_collection) && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              {vendor.home_collection && (
                <CollectionBadge active={isActive} tone="emerald" Icon={House} label="Home Collection" />
              )}
              {vendor.walkin_collection && (
                <CollectionBadge active={isActive} tone="indigo" Icon={Building2} label="Lab Collection" />
              )}
            </div>
          )}
        </div>

        {/* Radio indicator */}
        <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full transition-all ${
          isActive ? 'bg-orange-500' : 'bg-gray-100 group-hover:bg-orange-100'
        }`}>
          <AnimatePresence initial={false}>
            {isActive ? (
              <motion.span
                key="dot"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
                className="h-2 w-2 rounded-full bg-white"
              />
            ) : (
              <span className="h-2 w-2 rounded-full bg-gray-300" />
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default DiagnosticPartnerCard;
